// Single source of truth for every HTTP call in the app.
// Replaces the 76 hardcoded `http://117.247.193.197:8226` literals and the
// 22 copies of getAuthHeaders() that used to live inside each module.

import type { ListResponse, Page } from '../types/common';

const RAW_BASE = process.env.REACT_APP_API_BASE_URL || 'http://117.247.193.197:8226';
export const BASE_URL = RAW_BASE.replace(/\/+$/, '');

export const TOKEN_KEY = 'church_erp_token';

export const getToken = (): string =>
  sessionStorage.getItem(TOKEN_KEY) || localStorage.getItem(TOKEN_KEY) || '';

export const getAuthHeaders = (): Record<string, string> => {
  const token = getToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {})
  };
};

export class ApiError extends Error {
  status: number;

  body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

/** `mobileNumber` -> `Mobile Number`, so the message names the box on screen. */
const humanise = (field: string): string =>
  field
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim();

/**
 * Turns the server's `{ mobileNumber: "must be ..." }` into
 * `Mobile Number: must be ...`. Caps at three so a form with many empty
 * required fields does not produce an unreadable wall of text.
 */
const formatFieldErrors = (fields?: Record<string, string>): string => {
  if (!fields) return '';
  const entries = Object.entries(fields);
  if (entries.length === 0) return '';

  const shown = entries.slice(0, 3)
    .map(([field, msg]) => `${humanise(field)}: ${msg}`)
    .join('; ');

  const rest = entries.length - 3;
  return rest > 0 ? `${shown} (and ${rest} more)` : shown;
};

/** Values accepted as query-string parameters. */
export type QueryParams = Record<string, string | number | boolean | null | undefined>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Serialised to JSON unless already a string. */
  body?: unknown;
  params?: QueryParams;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /** Skip the Authorization header and the 401 auto-logout (login only). */
  skipAuth?: boolean;
}

/** Options callers pass through; `method` and `body` are set by the helpers. */
export type CallOptions = Omit<RequestOptions, 'method' | 'body'>;

// Session expired -> wipe and bounce to login. One place, not 22.
const handleUnauthorized = (): void => {
  sessionStorage.clear();
  localStorage.removeItem(TOKEN_KEY);
  window.location.reload();
};

const buildUrl = (path: string, params?: QueryParams): string => {
  const clean = path.startsWith('http')
    ? path
    : `${BASE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
  if (!params) return clean;

  const query = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join('&');

  return query ? `${clean}${clean.includes('?') ? '&' : '?'}${query}` : clean;
};

/**
 * The generic is a claim about what the server returns, not a guarantee —
 * nothing validates the response shape at runtime. See the caveat at the top
 * of types/common.ts.
 */
// When a save was last held for approval (ms) — useNotification uses it to
// turn the screen's "saved" message that follows into "sent for approval".
let lastHeldAt = 0;

/** True once, if a save was held for approval in the last few seconds. */
export const takeRecentHold = (withinMs = 4000): boolean => {
  const recent = lastHeldAt > 0 && Date.now() - lastHeldAt < withinMs;
  lastHeldAt = 0;
  return recent;
};

async function request<T = unknown>(
  path: string,
  { method = 'GET', body, params, headers, signal, skipAuth = false }: RequestOptions = {}
): Promise<T> {
  const response = await fetch(buildUrl(path, params), {
    method,
    signal,
    headers: {
      ...(skipAuth ? { 'Content-Type': 'application/json' } : getAuthHeaders()),
      ...headers
    },
    ...(body !== undefined
      ? { body: typeof body === 'string' ? body : JSON.stringify(body) }
      : {})
  });

  // A save held for approval (e.g. a delete) — ApprovalNotice shows it on any
  // screen. Only saves carry one; reads never do.
  const approvalNotice = method !== 'GET' ? response.headers.get('X-Approval') : null;
  if (approvalNotice && typeof window !== 'undefined') {
    lastHeldAt = Date.now();
    window.dispatchEvent(new CustomEvent('approval-notice', { detail: approvalNotice }));
  }
  // Information about approvals that is not a hold (a delete cancelled the
  // changes waiting on the record; a restore does not bring them back).
  const approvalInfo = method !== 'GET' ? response.headers.get('X-Approval-Info') : null;
  if (approvalInfo && typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('approval-info', { detail: approvalInfo }));
  }

  if (response.status === 401 && !skipAuth) {
    handleUnauthorized();
    throw new ApiError('Session expired. Please sign in again.', 401, null);
  }

  // 204 No Content / empty body (common on DELETE)
  if (response.status === 204 || response.headers.get('content-length') === '0') {
    if (!response.ok) {
      throw new ApiError(`Request failed (${response.status})`, response.status, null);
    }
    return null as unknown as T;
  }

  const contentType = response.headers.get('content-type') || '';
  const payload: unknown = contentType.includes('application/json')
    ? await response.json().catch(() => null)
    : await response.text().catch(() => null);

  if (!response.ok) {
    const asRecord = payload as
      { message?: string; error?: string; fields?: Record<string, string> } | null;
    const base =
      asRecord?.message ||
      asRecord?.error ||
      (typeof payload === 'string' && payload) ||
      `Request failed (${response.status})`;

    // A rejected save carries a per-field reason. Without folding it into the
    // message the user only ever sees "Validation failed" and has no idea which
    // box to correct — every module surfaces `err.message` and nothing else.
    const detail = formatFieldErrors(asRecord?.fields);
    throw new ApiError(detail ? `${base} — ${detail}` : base, response.status, payload);
  }

  return payload as T;
}

export const api = {
  request,
  get: <T = unknown>(path: string, options?: CallOptions) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T = unknown>(path: string, body?: unknown, options?: CallOptions) =>
    request<T>(path, { ...options, method: 'POST', body }),
  put: <T = unknown>(path: string, body?: unknown, options?: CallOptions) =>
    request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T = unknown>(path: string, body?: unknown, options?: CallOptions) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  del: <T = void>(path: string, options?: CallOptions) =>
    request<T>(path, { ...options, method: 'DELETE' })
};

const isPage = <T>(data: unknown): data is Page<T> =>
  Boolean(data) && Array.isArray((data as Page<T>)?.content);

/**
 * Spring Boot returns either a plain array or a Page<{content, totalElements}>.
 * Every module was re-implementing these two helpers.
 */
export const getList = <T = unknown>(data: ListResponse<T> | unknown): T[] => {
  if (Array.isArray(data)) return data as T[];
  if (isPage<T>(data)) return data.content;
  return [];
};

export const getTotal = <T = unknown>(data: ListResponse<T> | unknown): number => {
  const total = (data as Page<T> | null)?.totalElements;
  if (total !== undefined) return total;
  return getList<T>(data).length;
};

/**
 * Never rejects — returns [] instead. For dashboard-style parallel loads
 * where one dead endpoint must not blank the whole screen.
 */
export const safeGet = <T = unknown>(path: string, options?: CallOptions): Promise<T[]> =>
  api
    .get<ListResponse<T>>(path, options)
    .then((data) => getList<T>(data))
    .catch(() => [] as T[]);

export default api;
